import * as queryString from 'query-string';

import { PlayerProfileTabIdentifiers } from './constants';
import { savedListTypes, getPlayerName } from './index';

export const getSavedPlayersMenuPath = (savedListEnabled, claimedPlayer) => {
    if (savedListEnabled && claimedPlayer?.playerId) {
        return `/players/${claimedPlayer.playerId}?t=${PlayerProfileTabIdentifiers.SAVED_LISTS}`;
    }
    return '/saved/players';
};

// The Saved Player List tab reads its selected list off the URL (?t=8&listid={id}), so deep-link
// entry (digest email #2) and in-app selection are one code path. sort:false keeps the contract
// order `?t=8&listid={id}` the email pins.
export const buildSavedListSearch = (listId) => (
    `?${queryString.stringify({
        t: PlayerProfileTabIdentifiers.SAVED_LISTS,
        listid: listId,
    }, { sort: false })}`
);

// Full deep link to a viewer's own saved list, used by the off-profile "View List" toasts. Uses the
// canonical /profiles/ form (not the legacy /players/ 301) so every entry point — Hub row, digest
// email, and toast — resolves to the identical URL shape and one code path.
export const getSavedListDeepLinkPath = (claimedPlayer, listId) => (
    `/profiles/${claimedPlayer.playerId}${buildSavedListSearch(listId)}`
);

export const getSavedPlayersRedirectPath = (savedListEnabled, claimedPlayer, isPlayersList) => {
    if (isPlayersList && savedListEnabled && claimedPlayer?.playerId) {
        return getSavedPlayersMenuPath(savedListEnabled, claimedPlayer);
    }
    return null;
};

const DEFAULT_TITLE = 'Manage Lists';

const firstValue = (value) => (Array.isArray(value) ? value[0] : value);

// Reads the selected saved list off the tab's query string. Returns a positive integer id, or null
// when the param is absent or not a valid id (which the tab treats as the default Hub view). A
// well-formed-but-nonexistent id passes here and is caught later by the detail fetch's 404/403.
export const parseSavedListIdParam = (search) => {
    const raw = firstValue(queryString.parse(search).listid);
    if (raw === undefined || raw === null || raw === '') {
        return null;
    }
    const id = Number(raw);
    return Number.isInteger(id) && id > 0 ? id : null;
};

const getTitleFromState = (savedItem, listType) => {
    if (listType === savedListTypes.players.key) {
        return getPlayerName(savedItem.firstName, savedItem.lastName);
    }
    if (listType === savedListTypes.schools.key) {
        return savedItem.name;
    }
    return DEFAULT_TITLE;
};

const getTitleFromQuery = (search, listType) => {
    const parsed = queryString.parse(search);
    const firstName = firstValue(parsed.firstName);
    const lastName = firstValue(parsed.lastName);
    const name = firstValue(parsed.name);
    if (listType === savedListTypes.players.key && (firstName || lastName)) {
        return getPlayerName(firstName, lastName);
    }
    if (listType === savedListTypes.schools.key && name) {
        return name;
    }
    return DEFAULT_TITLE;
};

// The legacy profile header pushes the subject's name as router state; the
// Next.js profile links here with a full page load, so that state is absent
// and the name arrives on the query string instead.
export const getSavedListPageTitle = (location, listType) => {
    if (!location || !listType) {
        return DEFAULT_TITLE;
    }
    if (location.state) {
        return getTitleFromState(location.state, listType);
    }
    return getTitleFromQuery(location.search, listType);
};
